/**
 * Widget Bridge - Event emitter to connect the global API (window.hiva)
 * with the internal React component state.
 * 
 * This allows external JS to control the widget programmatically:
 *   hiva('open')
 *   hiva('close')
 *   hiva('sendMessage', 'Hello')
 */

type WidgetCommand = 'open' | 'close' | 'sendMessage';
type CommandHandler = (...args: any[]) => void;

class WidgetBridge {
  private handlers: Map<WidgetCommand, CommandHandler> = new Map();

  /**
   * Register a handler for a command (called by React component)
   */
  on(command: WidgetCommand, handler: CommandHandler): void {
    this.handlers.set(command, handler);
  }

  /**
   * Remove a handler for a command
   */
  off(command: WidgetCommand): void {
    this.handlers.delete(command);
  }

  /**
   * Emit a command (called by the global API)
   */
  emit(command: WidgetCommand, ...args: any[]): boolean {
    const handler = this.handlers.get(command);
    if (handler) {
      handler(...args);
      return true;
    }
    return false;
  }

  /**
   * Check if a handler is registered for a command
   */
  has(command: WidgetCommand): boolean {
    return this.handlers.has(command);
  }
}

// Singleton instance shared between main.tsx and ChatWidget
export const widgetBridge = new WidgetBridge();
